import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { explorerOnboardingQueryKey } from "../auth/authSession";
import { getMyExplorerOnboarding } from "../api/explorerOnboardingApi";
import { hasExplorerOnboarding } from "../explorer/hasExplorerOnboarding";
import { useGetExplorerConnectionLimit } from "../api/queries/useGetExplorerConnectionLimit";

/**
 * Explorer-only weekly connection action budget (send + accept, max 3/week).
 * Paid members always have unlimited actions.
 */
export function useExplorerConnectionLimit() {
  const { objectId, user } = useAppContext();
  const userId = objectId ?? "";
  const hasPaid = user?.user?.hasPaid === 1;

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: isLoggedIn() && !!objectId && !hasPaid,
    retry: false,
    staleTime: 60_000,
  });

  const { data: limitData, refetch: refetchLimit } = useGetExplorerConnectionLimit({
    userId,
    enabled: isLoggedIn() && !!userId && !hasPaid,
  });

  const isExplorer = useMemo(() => {
    if (hasPaid) return false;
    if (limitData?.isExplorer === true) return true;
    return hasExplorerOnboarding(explorerMe);
  }, [hasPaid, limitData?.isExplorer, explorerMe]);

  const canPerformConnectionAction = useMemo(() => {
    if (!isExplorer) return true;
    return limitData?.canPerformAction !== false;
  }, [isExplorer, limitData?.canPerformAction]);

  return {
    isExplorer,
    canPerformConnectionAction,
    used: limitData?.used ?? 0,
    limit: limitData?.limit ?? 3,
    remaining: limitData?.remaining ?? 3,
    weekResetsUtc: limitData?.weekResetsUtc,
    refetchLimit,
  };
}
